import { useMemo } from 'react'
import { useCartaoAmareloMeList } from '@/api/queries/cartao-amarelo-queries'
import { useDDSMeList } from '@/api/queries/dds-queries'
import { useDirecionadaMeList } from '@/api/queries/direcionada/direcionada-queries'
import { useInteracaoMeList } from '@/api/queries/interacao-queries'
import { useVereAgirMeList } from '@/api/queries/ver-e-agir-queries'

export type InspectionKind =
	| 'seeAndAct'
	| 'directed'
	| 'yellowCard'
	| 'interaction'
	| 'dds'

export interface RecentInspectionItem {
	kind: InspectionKind
	id: string
	title: string
	date: string
}

interface KindSummary {
	currentMonth: number
	previousMonth: number
}

export interface PersonalInspectionsSummary {
	isLoading: boolean
	byKind: Record<InspectionKind, KindSummary>
	totalCurrentMonth: number
	totalPreviousMonth: number
	recentItems: RecentInspectionItem[]
}

function getMonthBounds(offsetFromCurrent: number) {
	const now = new Date()
	const start = new Date(
		now.getFullYear(),
		now.getMonth() + offsetFromCurrent,
		1,
	)
	const end = new Date(
		now.getFullYear(),
		now.getMonth() + offsetFromCurrent + 1,
		0,
	)
	return { start, end }
}

function isInMonth(dateStr: string, offsetFromCurrent: number): boolean {
	const { start, end } = getMonthBounds(offsetFromCurrent)
	const date = new Date(dateStr)
	return date >= start && date <= end
}

/**
 * Resumo pessoal (mês atual x mês anterior) das inspeções do usuário logado,
 * agregando os 5 tipos via seus endpoints `*MeList` (scope: 'mine').
 *
 * Limitação: os endpoints `*MeList` são cursor-based e retornam apenas a
 * primeira página — para usuários com volume muito alto de inspeções no
 * período, a contagem pode não capturar 100% dos registros mais antigos
 * dentro do mês anterior.
 */
export function usePersonalInspectionsSummary(): PersonalInspectionsSummary {
	const previousMonthStart = getMonthBounds(-1).start.toISOString().slice(0, 10)
	const currentMonthEnd = getMonthBounds(0).end.toISOString().slice(0, 10)

	const seeAndAct = useVereAgirMeList({
		scope: 'mine',
		startDate: previousMonthStart,
		endDate: currentMonthEnd,
	})
	const directed = useDirecionadaMeList({ scope: 'mine' })
	const yellowCard = useCartaoAmareloMeList({
		scope: 'mine',
		startDate: previousMonthStart,
		endDate: currentMonthEnd,
	})
	const interaction = useInteracaoMeList({ scope: 'mine' })
	const dds = useDDSMeList({ scope: 'mine' })

	const isLoading =
		seeAndAct.isLoading ||
		directed.isLoading ||
		yellowCard.isLoading ||
		interaction.isLoading ||
		dds.isLoading

	return useMemo(() => {
		const summarize = <T>(
			items: T[] | undefined,
			getDate: (item: T) => string,
		): KindSummary => {
			const list = items ?? []
			return {
				currentMonth: list.filter((i) => isInMonth(getDate(i), 0)).length,
				previousMonth: list.filter((i) => isInMonth(getDate(i), -1)).length,
			}
		}

		const byKind: Record<InspectionKind, KindSummary> = {
			seeAndAct: summarize(seeAndAct.data?.items, (i) => i.date),
			directed: summarize(directed.data?.items, (i) => i.createdAt),
			yellowCard: summarize(yellowCard.data?.items, (i) => i.date),
			interaction: summarize(interaction.data?.items, (i) => i.createdAt),
			dds: summarize(dds.data?.items, (i) => i.createdAt),
		}

		const totalCurrentMonth = Object.values(byKind).reduce(
			(sum, k) => sum + k.currentMonth,
			0,
		)
		const totalPreviousMonth = Object.values(byKind).reduce(
			(sum, k) => sum + k.previousMonth,
			0,
		)

		const recentItems: RecentInspectionItem[] = [
			...(seeAndAct.data?.items ?? []).map((i) => ({
				kind: 'seeAndAct' as const,
				id: i.id,
				title: `${i.kind.name} · ${i.cr.name}`,
				date: i.date,
			})),
			...(directed.data?.items ?? []).map((i) => ({
				kind: 'directed' as const,
				id: i.id,
				title: `${i.quizTitle} · ${i.crName}`,
				date: i.createdAt,
			})),
			...(yellowCard.data?.items ?? []).map((i) => ({
				kind: 'yellowCard' as const,
				id: i.id,
				title: `${i.kind.name} · ${i.cr.name}`,
				date: i.date,
			})),
			...(interaction.data?.items ?? []).map((i) => ({
				kind: 'interaction' as const,
				id: i.id,
				title: `${i.classification} · ${i.crName}`,
				date: i.createdAt,
			})),
			...(dds.data?.items ?? []).map((i) => ({
				kind: 'dds' as const,
				id: i.id,
				title: `${i.subject} · ${i.crName}`,
				date: i.createdAt,
			})),
		]
			.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
			.slice(0, 6)

		return {
			isLoading,
			byKind,
			totalCurrentMonth,
			totalPreviousMonth,
			recentItems,
		}
	}, [
		isLoading,
		seeAndAct.data,
		directed.data,
		yellowCard.data,
		interaction.data,
		dds.data,
	])
}
